import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { usePortfolioHoldings } from '@/hooks/usePortfolioHoldings';
import { Plus } from 'lucide-react';

interface AddHoldingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ASSET_TYPES = [
  { value: 'stock', label: 'Stock' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'forex', label: 'Forex' },
  { value: 'commodity', label: 'Commodity' },
  { value: 'etf', label: 'ETF' },
  { value: 'bond', label: 'Bond' },
];

export const AddHoldingModal: React.FC<AddHoldingModalProps> = ({ open, onOpenChange }) => {
  const { addHolding } = usePortfolioHoldings();
  const [symbol, setSymbol] = useState('');
  const [assetType, setAssetType] = useState('stock');
  const [quantity, setQuantity] = useState('');
  const [averagePrice, setAveragePrice] = useState('');
  const [notes, setNotes] = useState('');
  const [suggestions, setSuggestions] = useState<{ symbol: string; display_name: string }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (symbol.length < 1) {
      setSuggestions([]);
      return;
    }
    const timeout = setTimeout(async () => {
      const upperSymbol = symbol.toUpperCase();
      const [regResult, scResult] = await Promise.all([
        supabase.from('symbol_registry').select('symbol, display_name').ilike('symbol', `%${upperSymbol}%`).eq('is_active', true).limit(5),
        supabase.from('sector_classifications').select('symbol, sector').ilike('symbol', `%${upperSymbol}%`).limit(5),
      ]);

      const seen = new Set<string>();
      const combined: { symbol: string; display_name: string }[] = [];
      (regResult.data || []).forEach(r => { if (!seen.has(r.symbol)) { seen.add(r.symbol); combined.push(r); } });
      (scResult.data || []).forEach(r => { if (!seen.has(r.symbol)) { seen.add(r.symbol); combined.push({ symbol: r.symbol, display_name: r.sector }); } });
      setSuggestions(combined);
      setShowSuggestions(combined.length > 0);
    }, 300);
    return () => clearTimeout(timeout);
  }, [symbol]);

  const handleSubmit = () => {
    const qty = parseFloat(quantity);
    const price = parseFloat(averagePrice);
    if (!symbol || isNaN(qty) || qty <= 0 || isNaN(price) || price <= 0) return;

    addHolding.mutate({
      symbol: symbol.toUpperCase(),
      asset_type: assetType,
      quantity: qty,
      average_price: price,
    });

    setSymbol('');
    setQuantity('');
    setAveragePrice('');
    setNotes('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Plus className="h-5 w-5 text-primary" />
            Add Holding
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="relative">
            <Label htmlFor="symbol" className="text-muted-foreground">Symbol</Label>
            <Input
              id="symbol"
              placeholder="e.g. AAPL, BTC/USD"
              value={symbol}
              onChange={e => { setSymbol(e.target.value); setShowSuggestions(true); }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="bg-background"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md max-h-40 overflow-y-auto">
                {suggestions.map(s => (
                  <button
                    key={s.symbol}
                    className="w-full text-left px-3 py-2 hover:bg-accent text-sm text-foreground"
                    onMouseDown={() => { setSymbol(s.symbol); setShowSuggestions(false); }}
                  >
                    <span className="font-medium">{s.symbol}</span>
                    <span className="ml-2 text-muted-foreground">{s.display_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-muted-foreground">Asset Type</Label>
            <Select value={assetType} onValueChange={setAssetType}>
              <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ASSET_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="quantity" className="text-muted-foreground">Quantity</Label>
              <Input id="quantity" type="number" min="0" step="any" placeholder="0" value={quantity} onChange={e => setQuantity(e.target.value)} className="bg-background" />
            </div>
            <div>
              <Label htmlFor="avgPrice" className="text-muted-foreground">Avg Buy Price</Label>
              <Input id="avgPrice" type="number" min="0" step="any" placeholder="0.00" value={averagePrice} onChange={e => setAveragePrice(e.target.value)} className="bg-background" />
            </div>
          </div>

          <div>
            <Label htmlFor="notes" className="text-muted-foreground">Notes (optional)</Label>
            <Textarea id="notes" placeholder="Any notes..." value={notes} onChange={e => setNotes(e.target.value)} className="bg-background resize-none" rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={addHolding.isPending || !symbol || !quantity || !averagePrice}>
            {addHolding.isPending ? 'Adding...' : 'Add Holding'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
